<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import Button from '~/ui/Button.vue'
import Modal from '~/ui/Modal.vue'
import Spacer from '~/ui/Spacer.vue'
import Layout from '~/ui/Layout.vue'
import PopoverItem from '~/ui/popover/PopoverItem.vue'

interface Events {
  (e: 'confirm'): void
}

// Note that properties such as [disabled] and 'destructive' | 'primary' are inherited.
const props = defineProps<{
  title?: string
  action?:() => void,
  confirmColor?:'success' | 'danger',
  popoverItem?: boolean,
  icon?: string
}>()

const { t } = useI18n()

const emit = defineEmits<Events>()

const showModal = ref(false)

const canceled = ref(false)

const confirm = () => {
  showModal.value = false
  emit('confirm')
  props.action?.()
}
</script>

<template>
  <component
    :is="props.popoverItem ? PopoverItem : Button"
    :keep-open="props.popoverItem"
    :class="popoverItem ? 'color-destructive-ghost' : 'color-destructive-solid'"
    v-bind="$attrs"
    @click.prevent.stop="showModal = !showModal"
  >
    <Icon
      v-if="!props.popoverItem"
      :icon="icon ? icon : 'bi:exclamation-octagon-fill'"
    />
    <slot />
    <Modal
      v-if="!props.popoverItem"
      v-model="showModal"
      is-destructive
      :title="title || t('components.common.DangerousButton.header.confirm')"
      :cancel="t('components.common.DangerousButton.button.cancel')"
    >
      <Layout
        flex
        style="flex-wrap: nowrap;
        align-items: center;"
      >
        <Icon
          icon="bi:exclamation-octagon"
          style="font-size: 6rem; color: var(--fw-red-500);"
        />
        <div>
          <slot name="content" />
        </div>
      </Layout>
      <template #actions>
        <Spacer grow />
        <Button
          :class="confirmColor === 'success'
            ? 'color-primary-solid'
            : 'color-destructive-solid'
          "
          @click="confirm"
        >
          <slot name="confirm">
            {{ t('components.common.DangerousButton.button.confirm') }}
          </slot>
        </Button>
      </template>
    </Modal>
  </component>
  <template v-if="props.popoverItem && showModal">
    <Layout>
      <hr>
      <div
        v-if="$slots.content"
        style="font-size: 1rem;"
      >
        <slot name="content" />
      </div>
      <Layout
        flex
        gap-8
      >
        <Button
        :class="confirmColor === 'success'
          ? 'color-primary-solid'
          : 'color-destructive-solid'
        "
          @click="confirm"
        >
          <slot name="confirm">
            {{ t('components.common.DangerousButton.button.confirm') }}
          </slot>
        </Button>
        <Spacer
          h
          grow
        />
        <Button
          class="color-default-solid"
          autofocus
          ui-raised
          @click="showModal = false; canceled = true;"
        >
          <slot name="cancel">
            {{ t('components.common.DangerousButton.button.cancel') }}
          </slot>
        </Button>
      </Layout>
    </Layout>
  </template>
</template>
